import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { apiClient } from '../utils/api'
import { useAuth } from './AuthContext'
import { JobDescription } from '../types'

interface JobDescriptionContextType {
  jobDescriptions: JobDescription[]
  loading: boolean
  fetchJobDescriptions: () => Promise<void>
  addJobDescription: (jd: JobDescription) => void
  updateJobDescription: (id: string, jd: JobDescription) => void
}

const JobDescriptionContext = createContext<JobDescriptionContextType | undefined>(undefined)

export function JobDescriptionProvider({ children }: { children: ReactNode }) {
  const [jobDescriptions, setJobDescriptions] = useState<JobDescription[]>([])
  const [loading, setLoading] = useState(true)
  const { user, loading: authLoading } = useAuth()

  // Memoized fetch function that doesn't depend on user state
  const fetchJobDescriptions = useCallback(async () => {
    try {
      setLoading(true)
      const result = await apiClient.get<{ success: boolean; data: JobDescription[]; message: string }>('/job-descriptions')
      setJobDescriptions(result.data || [])
    } catch (error) {
      console.error('Error fetching job descriptions:', error)
      setJobDescriptions([])
    } finally {
      setLoading(false)
    }
  }, []) // No dependencies - this function is stable

  // Effect that handles authentication-dependent fetching
  useEffect(() => {
    // Don't fetch if auth is still loading
    if (authLoading) {
      console.log('Auth still loading, skipping job descriptions fetch');
      return;
    }
    
    // Don't fetch if user is not authenticated
    if (!user) {
      console.log('No user authenticated, clearing job descriptions');
      setJobDescriptions([]);
      setLoading(false);
      return;
    }

    // Additional safety check: ensure we have a valid user with an ID
    if (!user.id) {
      console.log('User has no ID, skipping job descriptions fetch');
      setJobDescriptions([]);
      setLoading(false);
      return;
    }

    console.log('User authenticated, fetching job descriptions');
    // Fetch job descriptions when user is authenticated
    fetchJobDescriptions();
  }, [user, authLoading, fetchJobDescriptions])

  const addJobDescription = useCallback((jd: JobDescription) => {
    setJobDescriptions(prev => [...prev, jd])
  }, [])

  const updateJobDescription = useCallback((id: string, updatedJd: JobDescription) => {
    setJobDescriptions(prev => prev.map(jd => jd.id === id ? updatedJd : jd))
  }, [])

  return (
    <JobDescriptionContext.Provider value={{
      jobDescriptions,
      loading,
      fetchJobDescriptions,
      addJobDescription,
      updateJobDescription
    }}>
      {children}
    </JobDescriptionContext.Provider>
  )
}

export function useJobDescriptions() {
  const context = useContext(JobDescriptionContext)
  if (context === undefined) {
    throw new Error('useJobDescriptions must be used within a JobDescriptionProvider')
  }
  return context
} 